import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2, Check, X, MinusCircle, FileBadge2, Camera, Trash2, FileText, ExternalLink, ShieldAlert, ShieldCheck, HardHat, AlertTriangle, Lock, FileStack } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import RiskAssessmentDialog from "@/components/RiskAssessmentDialog";

export default function InspectionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = ["admin","super_admin"].includes(user?.role) || user?.role === "inspector";
  const [insp, setInsp] = useState(null);
  const [asset, setAsset] = useState(null);
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [refDocs, setRefDocs] = useState([]);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [raOpen, setRaOpen] = useState(false);
  const [raReadOnly, setRaReadOnly] = useState(false);
  const [raHistoryOpen, setRaHistoryOpen] = useState(false);

  const loadRA = async () => {
    try {
      const { data } = await api.get(`/risk-assessments/${id}`);
      setRiskAssessment(data);
    } catch { setRiskAssessment(null); }
  };

  const load = async () => {
    const { data } = await api.get(`/inspections/${id}`);
    setInsp(data);
    setNotes(data.notes || "");
    setChecklist(data.checklist || []);
    if (data.asset_id) {
      const a = await api.get(`/assets/${data.asset_id}`);
      setAsset(a.data);
      try {
        const r = await api.get("/documents", { params: { asset_category: a.data.category } });
        setRefDocs(r.data);
      } catch (err) { console.debug("reference documents unavailable", err?.message); }
    }
    await loadRA();
  };

  useEffect(() => { load(); }, [id]);

  const setResult = (idx, result) => setChecklist(checklist.map((c, i) => i === idx ? { ...c, result } : c));
  const setItemNotes = (idx, txt) => setChecklist(checklist.map((c, i) => i === idx ? { ...c, notes: txt } : c));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/inspections/${id}`, { checklist, notes, ...extra });
      setInsp(data);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  const generateAi = async () => {
    setAiLoading(true);
    try {
      await save();
      const { data } = await api.post("/ai/inspection-summary", { inspection_id: id });
      setInsp((prev) => ({ ...prev, ai_summary: data.summary }));
      toast.success("AI summary generated");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e.response?.status === 402 ? "AI credit exhausted. Top up your Emergent LLM key balance." : "AI failed");
      toast.error(msg);
    } finally { setAiLoading(false); }
  };

  const complete = async (result) => {
    await save({ status: "completed", result });
    toast.success(`Marked ${result.toUpperCase()}`);
  };

  if (!insp) return <div className="p-8 text-gray-500">Loading…</div>;
  const passed = checklist.filter(c => c.result === 'pass').length;
  const failed = checklist.filter(c => c.result === 'fail').length;
  const na = checklist.filter(c => c.result === 'n/a').length;
  const raBlocked = !riskAssessment || riskAssessment.overall_verdict === "stop";
  const raWarning = riskAssessment?.overall_verdict === "requires_mitigation";

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="inspection-detail-page">
      <button onClick={() => nav('/inspections')} className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-600 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Back to Inspections</button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1 font-mono">Inspection · {id.slice(-6).toUpperCase()}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{asset?.name || 'Asset'}</h1>
          <div className="flex gap-4 mt-2 text-sm text-gray-600">
            <span className="font-mono">{asset?.asset_tag}</span>
            <span>·</span>
            <span>{asset?.location}</span>
            <span>·</span>
            <span className="capitalize">{insp.inspection_type?.replace('_',' ')}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill v={insp.status} />
          {insp.result && <ResultPill v={insp.result} />}
          <Button
            variant="outline"
            size="sm"
            data-testid="job-card-tpl-btn"
            className="rounded-full border-gray-300 h-9"
            onClick={async () => {
              try {
                const res = await api.get(`/templates/render/job-card/${id}`, { responseType: "blob" });
                const blob = res.data;
                const ct = blob.type || "";
                const ext = ct.includes("word") ? "docx" : ct.includes("pdf") ? "pdf" : "html";
                const url = URL.createObjectURL(blob);
                if (ext === "docx") {
                  const a = document.createElement("a"); a.href = url; a.download = `job-card-${id.slice(-6)}.${ext}`; a.click();
                } else {
                  window.open(url, "_blank", "noopener");
                }
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) {
                const raw = e.response?.data;
                const msg = raw instanceof Blob ? await raw.text() : (raw?.detail || "No template configured");
                try { toast.error(JSON.parse(msg).detail || msg); } catch { toast.error(msg); }
              }
            }}
          >
            <FileStack className="w-4 h-4 mr-2" /> Job Card
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatChip color="bg-[#059669]" label="Pass" value={passed} />
        <StatChip color="bg-[#DC2626]" label="Fail" value={failed} />
        <StatChip color="bg-gray-400" label="N/A" value={na} />
      </div>

      {/* Mandatory Pre-Task Risk Assessment */}
      <RiskAssessmentCard
        ra={riskAssessment}
        canEdit={canEdit}
        onStart={() => { setRaReadOnly(false); setRaOpen(true); }}
        onView={() => { setRaReadOnly(true); setRaOpen(true); }}
        onHistory={() => setRaHistoryOpen(true)}
      />

      {/* Checklist */}
      <div className={`bg-white border border-gray-200 mb-6 relative ${raBlocked ? "opacity-60" : ""}`} data-testid="checklist-card">
        {raBlocked && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <div className="bg-white border border-[#DC2626] px-4 py-3 flex items-center gap-3 shadow-sm">
              <Lock className="w-4 h-4 text-[#DC2626]" />
              <span className="text-sm font-semibold">Complete the risk assessment to unlock the checklist.</span>
            </div>
          </div>
        )}
        <div className="px-5 py-4 border-b border-gray-200"><h2 className="font-display text-lg font-bold tracking-tight">Checklist</h2></div>
        <div className="divide-y divide-gray-100">
          {checklist.map((c, idx) => (
            <div key={c.id || `cl-${idx}`} className="p-4" data-testid={`checklist-item-${idx}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-medium text-sm">{c.label}</div>
                  {canEdit && (
                    <Textarea value={c.notes || ""} onChange={(e) => setItemNotes(idx, e.target.value)} placeholder="Notes (optional)…" className="mt-2 rounded-md text-sm min-h-[60px]" />
                  )}
                  <PhotoRow item={c} idx={idx} canEdit={canEdit} onAdd={(fid) => { const photos = [...(c.photos || []), fid]; setChecklist(checklist.map((x,i) => i===idx ? { ...x, photos } : x)); }} />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {["pass","fail","n/a"].map(r => (
                    <button
                      key={r}
                      data-testid={`checklist-${idx}-${r}`}
                      disabled={!canEdit || raBlocked}
                      onClick={() => setResult(idx, r)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold border transition-colors ${
                        c.result === r
                          ? r === 'pass' ? 'bg-[#059669] text-white border-[#059669]'
                            : r === 'fail' ? 'bg-[#DC2626] text-white border-[#DC2626]'
                            : 'bg-gray-500 text-white border-gray-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {r === 'pass' ? <Check className="w-3.5 h-3.5" /> : r === 'fail' ? <X className="w-3.5 h-3.5" /> : <MinusCircle className="w-3.5 h-3.5" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white border border-gray-200 mb-6">
        <div className="px-5 py-4 border-b border-gray-200"><h2 className="font-display text-lg font-bold tracking-tight">Inspector Notes</h2></div>
        <div className="p-5">
          <Textarea data-testid="inspection-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canEdit} placeholder="Add overall inspection notes…" className="rounded-md min-h-[100px]" />
        </div>
      </div>

      {/* Reference Documents */}
      <div className="bg-white border border-gray-200 mb-6" data-testid="reference-docs-panel">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#01ADEF]" />
            <h2 className="font-display text-lg font-bold tracking-tight">Reference Documents</h2>
          </div>
          <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{refDocs.length} document{refDocs.length !== 1 ? "s" : ""} for {asset?.category?.replace('_',' ')}</span>
        </div>
        <div className="p-5">
          {refDocs.length === 0 ? (
            <p className="text-sm text-gray-500">No documents linked to this asset category yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {refDocs.slice(0, 6).map(d => (
                <a
                  key={d.id}
                  href={`${API}/uploads/${d.file_id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`ref-doc-${d.id}`}
                  className="flex items-center gap-3 border border-gray-200 p-3 hover:border-[#01ADEF] transition-colors group"
                >
                  <div className="w-8 h-8 bg-[#F0F7FF] border border-[#DBEAFE] flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-[#01ADEF]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{d.title}</div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">{d.category} · {d.uploader_name || "—"}</div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#01ADEF]" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      <InspectionAttachments iid={id} attachments={insp.attachments || []} canEdit={canEdit} onChange={load} />

      {/* AI Summary */}
      <div className="bg-gradient-to-br from-white to-[#F8FAFF] border-l-4 border-[#01ADEF] border border-gray-200 mb-6">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#01ADEF]" />
            <h2 className="font-display text-lg font-bold tracking-tight">AI Inspection Summary</h2>
          </div>
          {canEdit && (
            <Button data-testid="generate-ai-button" onClick={generateAi} disabled={aiLoading} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> {insp.ai_summary ? "Regenerate" : "Generate"}</>}
            </Button>
          )}
        </div>
        <div className="p-5">
          {insp.ai_summary ? (
            <pre className="font-sans text-sm text-gray-800 whitespace-pre-wrap leading-relaxed" data-testid="ai-summary-output">{insp.ai_summary}</pre>
          ) : (
            <p className="text-sm text-gray-500">No AI summary yet. Complete the checklist and click <b>Generate</b> to produce a professional inspection report.</p>
          )}
        </div>
      </div>

      {/* Actions */}
      {canEdit && (
        <div className="bg-white border border-gray-200 p-5 flex flex-wrap items-center gap-3">
          <Button data-testid="save-inspection" onClick={() => save()} disabled={saving} variant="outline" className="rounded-full">{saving ? "Saving..." : "Save Progress"}</Button>
          <Button data-testid="mark-pass" onClick={() => complete('pass')} disabled={raBlocked} className="rounded-md bg-[#059669] hover:bg-[#047857] disabled:opacity-50 disabled:cursor-not-allowed"><Check className="w-4 h-4 mr-2" /> Mark PASS &amp; Complete</Button>
          <Button data-testid="mark-fail" onClick={() => complete('fail')} disabled={raBlocked} className="rounded-md bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 disabled:cursor-not-allowed"><X className="w-4 h-4 mr-2" /> Mark FAIL</Button>
          {raBlocked && <span className="text-xs text-[#B91C1C] flex items-center gap-1"><Lock className="w-3 h-3" />Risk assessment required</span>}
          {raWarning && !raBlocked && <span className="text-xs text-[#92400E] flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Proceed with caution — mitigation required</span>}
          {insp.result === 'pass' && (
            <Link to="/certificates" state={{ prefill: { asset_id: insp.asset_id, inspection_id: id } }} className="ml-auto">
              <Button data-testid="issue-cert-btn" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><FileBadge2 className="w-4 h-4 mr-2" /> Issue Certificate</Button>
            </Link>
          )}
        </div>
      )}

      <RiskAssessmentDialog
        open={raOpen}
        onOpenChange={setRaOpen}
        inspectionId={id}
        assetCategory={asset?.category}
        existing={raReadOnly ? riskAssessment : (riskAssessment && !raReadOnly ? riskAssessment : null)}
        readOnly={raReadOnly}
        onSubmitted={() => { loadRA(); load(); }}
      />
      <RiskAssessmentHistoryDialog open={raHistoryOpen} onOpenChange={setRaHistoryOpen} inspectionId={id} />
    </div>
  );
}

function RiskAssessmentHistoryDialog({ open, onOpenChange, inspectionId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get(`/risk-assessments/${inspectionId}/history`).then(r => setItems(r.data)).catch(() => setItems([]));
  }, [open, inspectionId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Risk Assessment History</DialogTitle></DialogHeader>
        <div className="py-2 max-h-[60vh] overflow-y-auto" data-testid="ra-history-list">
          {items.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500">No historical revisions — this is the first submission.</div>
          ) : (
            <div className="space-y-3">
              {items.map((h, idx) => (
                <div key={h.id} className="border-l-2 border-gray-300 pl-4 py-2" data-testid={`ra-history-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">Revision archived on <span className="font-mono">{h.archived_at?.slice(0, 19).replace('T', ' ')}</span></div>
                    <VerdictPill v={h.overall_verdict} />
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Submitted <b>{h.submitted_at?.slice(0, 10)}</b> by <b>{h.inspector_name || "—"}</b>
                    · Max score <span className="font-mono font-bold">{h.max_risk_score}</span>
                    {h.ppe_missing?.length > 0 && <> · <span className="text-[#B91C1C]">{h.ppe_missing.length} PPE missing</span></>}
                  </div>
                  {h.notes && <div className="text-xs text-gray-700 mt-1 italic">&ldquo;{h.notes}&rdquo;</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter><Button className="rounded-md" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const VerdictPill = ({ v }) => {
  const map = {
    safe_to_proceed: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]", "Safe"],
    requires_mitigation: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]", "Mitigation"],
    stop: ["bg-[#FEE2E2]", "text-[#B91C1C]", "border-[#FCA5A5]", "STOP"],
  };
  const [bg, tx, br, label] = map[v] || map.safe_to_proceed;
  return <span className={`text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{label}</span>;
};

const RiskAssessmentCard = ({ ra, canEdit, onStart, onView, onHistory }) => {
  if (!ra) {
    return (
      <div className="border-2 border-dashed border-[#DC2626] bg-[#FEF2F2] p-5 mb-6 flex items-start gap-4" data-testid="ra-card-missing">
        <div className="w-10 h-10 bg-[#DC2626] flex items-center justify-center shrink-0"><HardHat className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold tracking-tight">Pre-Task Risk Assessment Required</div>
          <p className="text-sm text-gray-700 mt-1">Complete this mandatory safety assessment before starting the inspection. The checklist and pass/fail actions are locked until it&apos;s submitted with an acceptable verdict.</p>
        </div>
        {canEdit && <Button data-testid="start-ra-btn" onClick={onStart} className="rounded-md bg-[#DC2626] hover:bg-[#B91C1C]"><HardHat className="w-4 h-4 mr-2" />Start Assessment</Button>}
      </div>
    );
  }
  const v = ra.overall_verdict;
  const map = {
    safe_to_proceed: { color: "bg-[#059669]", border: "border-[#6EE7B7]", bg: "bg-[#F0FDF4]", label: "Safe to Proceed", icon: ShieldCheck },
    requires_mitigation: { color: "bg-[#D97706]", border: "border-[#FCD34D]", bg: "bg-[#FEFCE8]", label: "Requires Mitigation", icon: AlertTriangle },
    stop: { color: "bg-[#DC2626]", border: "border-[#FCA5A5]", bg: "bg-[#FEF2F2]", label: "STOP — Do Not Proceed", icon: ShieldAlert },
  }[v] || {};
  const Icon = map.icon || ShieldCheck;
  return (
    <div className={`border ${map.border} ${map.bg} p-5 mb-6 flex items-start gap-4`} data-testid="ra-card-submitted">
      <div className={`w-10 h-10 ${map.color} flex items-center justify-center shrink-0`}><Icon className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="font-display text-lg font-bold tracking-tight">Risk Assessment — {map.label}</div>
          <span data-testid={`ra-verdict-badge-${v}`} className={`text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 ${map.color}`}>{v.replace(/_/g, " ")}</span>
        </div>
        <div className="text-xs text-gray-700 mt-1 flex items-center gap-4 flex-wrap">
          <span>Submitted by <b>{ra.inspector_name}</b></span>
          <span>·</span>
          <span>{new Date(ra.submitted_at).toLocaleString()}</span>
          <span>·</span>
          <span>Max risk score <span className="font-mono font-bold">{ra.max_risk_score}</span></span>
          {ra.ppe_missing?.length > 0 && (<><span>·</span><span className="text-[#B91C1C]">{ra.ppe_missing.length} PPE missing</span></>)}
        </div>
        {v === "stop" && <p className="text-sm text-[#B91C1C] mt-2 font-semibold">Inspection cannot proceed. Reassess conditions and resubmit.</p>}
      </div>
      <div className="flex items-center gap-2">
        <Button data-testid="ra-history-btn" onClick={onHistory} variant="outline" className="rounded-full border-gray-300">History</Button>
        <Button data-testid="view-ra-btn" onClick={onView} variant="outline" className="rounded-full border-gray-300">View</Button>
        {canEdit && <Button data-testid="reassess-ra-btn" onClick={onStart} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Reassess</Button>}
      </div>
    </div>
  );
};

function InspectionAttachments({ iid, attachments, canEdit, onChange }) {
  const [busy, setBusy] = useState(false);
  const [cam, setCam] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/uploads/photo`, { method: "POST", body: fd, credentials: "include" });
      const d = await res.json();
      if (d.file_id) {
        await api.post(`/inspections/${iid}/attachments`, { file_id: d.file_id, filename: file.name, content_type: file.type });
        toast.success("Attached to inspection");
        onChange();
      }
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  const onPick = (e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; };
  const del = async (fid) => { try { await api.delete(`/inspections/${iid}/attachments/${fid}`); onChange(); } catch { toast.error("Failed"); } };
  const setCaption = async (fid, caption) => { try { await api.patch(`/inspections/${iid}/attachments/${fid}`, { caption }); onChange(); } catch { toast.error("Failed to save caption"); } };
  const startCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = s; setCam(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 100);
    } catch { toast.error("Camera unavailable — use file upload instead"); }
  };
  const stopCam = () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; setCam(false); };
  const snap = async () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement("canvas"); c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob(async (blob) => { const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" }); stopCam(); await uploadFile(file); }, "image/jpeg", 0.9);
  };

  return (
    <div className="bg-white border border-gray-200 mb-6" data-testid="inspection-attachments-panel">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileStack className="w-4 h-4 text-[#01ADEF]" />
          <h2 className="font-display text-lg font-bold tracking-tight">Inspection Attachments</h2>
        </div>
        <span className="text-[11px] text-gray-500">Images here print on the issued certificate</span>
      </div>
      <div className="p-5">
        {attachments.length === 0 && <p className="text-sm text-gray-500 mb-3">No files attached yet.</p>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {attachments.map((a) => {
            const isImg = (a.content_type || "").startsWith("image/");
            return (
              <div key={a.file_id} className="border border-gray-200 rounded-md overflow-hidden group relative" data-testid={`attachment-${a.file_id.slice(0,6)}`}>
                <a href={`${API}/uploads/${a.file_id}`} target="_blank" rel="noreferrer" className="block">
                  {isImg
                    ? <img src={`${API}/uploads/${a.file_id}`} alt={a.filename || ""} className="w-full h-24 object-cover" />
                    : <div className="w-full h-24 flex flex-col items-center justify-center bg-gray-50 text-gray-400"><FileText className="w-6 h-6" /><span className="text-[10px] mt-1 px-1 truncate max-w-full">{a.filename || "file"}</span></div>}
                </a>
                {isImg && (canEdit
                  ? <input data-testid={`attachment-caption-${a.file_id.slice(0,6)}`} defaultValue={a.caption || ""} onBlur={(e) => setCaption(a.file_id, e.target.value)} placeholder="Add a caption…" className="w-full text-[11px] px-2 py-1 border-t border-gray-100 outline-none focus:bg-[#F0F9FF]" />
                  : (a.caption ? <div className="text-[11px] px-2 py-1 text-gray-600 border-t border-gray-100 truncate">{a.caption}</div> : null))}
                {canEdit && <button data-testid={`attachment-del-${a.file_id.slice(0,6)}`} onClick={() => del(a.file_id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-gray-500 hover:text-[#DC2626] opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            );
          })}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-300 text-sm cursor-pointer hover:border-[#01ADEF]" data-testid="attachment-upload-label">
              <input type="file" className="hidden" onChange={onPick} disabled={busy} />
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Upload file
            </label>
            <Button type="button" data-testid="attachment-camera-btn" onClick={startCam} variant="outline" className="rounded-full border-gray-300"><Camera className="w-4 h-4 mr-2" /> Take photo</Button>
          </div>
        )}
      </div>

      <Dialog open={cam} onOpenChange={(o) => { if (!o) stopCam(); }}>
        <DialogContent className="rounded-md max-w-lg" data-testid="camera-dialog">
          <DialogHeader><DialogTitle className="font-display">Take a photo</DialogTitle></DialogHeader>
          <div className="bg-black rounded-md overflow-hidden"><video ref={videoRef} autoPlay playsInline className="w-full max-h-[50vh]" /></div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={stopCam}>Cancel</Button>
            <Button data-testid="camera-capture-btn" onClick={snap} disabled={busy} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{busy ? "Saving…" : "Capture & attach"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatChip = ({ color, label, value }) => (
  <div className="bg-white border border-gray-200 p-4 flex items-center gap-3">
    <div className={`w-10 h-10 ${color} flex items-center justify-center text-white font-display font-bold text-lg`}>{value}</div>
    <div className="text-xs uppercase tracking-wider font-semibold text-gray-600">{label}</div>
  </div>
);

function PhotoRow({ item, idx, canEdit, onAdd }) {
  const [uploading, setUploading] = useState(false);
  const photos = item.photos || [];
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/uploads/photo`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (data.file_id) onAdd(data.file_id);
    } finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      {photos.map((fid) => (
        <a key={fid} href={`${API}/uploads/${fid}`} target="_blank" rel="noreferrer" className="w-14 h-14 border border-gray-200 overflow-hidden hover:opacity-80" data-testid={`photo-${idx}-${fid.slice(0,6)}`}>
          <img src={`${API}/uploads/${fid}`} alt="" className="w-full h-full object-cover" />
        </a>
      ))}
      {canEdit && (
        <label className="w-14 h-14 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:text-[#01ADEF] hover:border-[#01ADEF] cursor-pointer" data-testid={`photo-upload-${idx}`}>
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} />
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        </label>
      )}
    </div>
  );
}
const StatusPill = ({ v }) => {
  const map = { scheduled: ["bg-[#E0F2FE]","text-[#075985]","border-[#7DD3FC]"], in_progress: ["bg-[#FEF3C7]","text-[#92400E]","border-[#FCD34D]"], completed: ["bg-[#D1FAE5]","text-[#065F46]","border-[#6EE7B7]"] };
  const [bg,tx,br] = map[v] || map.scheduled;
  return <span className={`inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{v?.replace('_',' ')}</span>;
};
const ResultPill = ({ v }) => v === 'pass'
  ? <span className="inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]">Pass</span>
  : <span className="inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]">Fail</span>;
